<template>
  <div class="form-field-wrapper">
    <!-- Label toujours visible au-dessus -->
    <div v-if="showLabel" class="form-field-label-container">
      <div class="form-field-label-content">
        <span class="form-field-label-text">
          {{ localizedLabel }}
          <AlertCircle
            v-if="isRequired"
            :size="16"
            class="inline text-red-500 ml-2"
          />
        </span>

        <Tooltip
          v-if="tooltipText || localizedHelp"
          :tooltip-content="tooltipText"
          :help-text-content="localizedHelp"
          :position="tooltipPosition"
        >
          <MessageCircle
            :size="18"
            class="text-gray-500 hover:text-gray-700 cursor-help transition-colors duration-200"
            tabindex="0"
            aria-label="Information supplémentaire"
          />
        </Tooltip>
      </div>
    </div>

    <!-- Input dans un conteneur séparé -->
    <div class="form-field-input-container">
      <slot />
    </div>

    <!-- Messages d'erreur -->
    <div v-if="errorMessages.length > 0" class="form-field-error-container">
      <p
        v-for="(msg, idx) in errorMessages"
        :key="idx"
        class="form-field-error-message"
      >
        {{ msg }}
      </p>
    </div>

    <!-- Exemple -->
    <div v-if="field.example" class="form-field-example-container">
      <p class="form-field-example-text">
        Exemple: {{ getLocalizedText(field.example, currentLang) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useForm, useField } from 'vee-validate';
import { AlertCircle, MessageCircle } from "lucide-vue-next";

import Tooltip from './components/Tooltip.vue';
import type { FormField } from './types/formTypeStructure';
import { getLocalizedText } from './utils/formHelpers';

const props = defineProps<{
  field: FormField;
  currentLang?: string;
  showLabel?: boolean;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
}>();

const currentLang = props.currentLang || 'fr';
const showLabel = props.showLabel !== false;

const { meta, errorMessage } = useField(() => props.field.fieldName || '');
const { meta: formMeta, submitCount } = useForm();

const isRequired = computed(() =>
  props.field.validations?.some((v) => v.validationType === 'required')
);

const tooltipText = computed(() =>
  getLocalizedText(props.field.tooltip, currentLang)
);
const localizedHelp = computed(() =>
  getLocalizedText(props.field.helpText, currentLang)
);
const localizedLabel = computed(() =>
  getLocalizedText(props.field.label, currentLang)
);

// Afficher l'erreur si : touché, soumis, ou submitCount > 0
const showError = computed(
  () => (meta.touched && meta.valid === false) || submitCount.value > 0
);

const errorMessages = computed(() =>
  showError.value && errorMessage.value ? [errorMessage.value] : []
);
</script>

<style scoped>
/* Conteneur principal */
.form-field-wrapper {
  position: relative;
  display: block;
  margin-bottom: 1.5rem;
  width: 100%;
}

/* Conteneur du label */
.form-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.form-field-label-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}

/* Conteneur de l'input */
.form-field-input-container {
  position: relative;
  display: block;
  margin-top: 0.5rem;
}

/* Conteneur des erreurs */
.form-field-error-container {
  margin-top: 0.5rem;
}

.form-field-error-message {
  font-size: 0.875rem;
  color: #ef4444;
  margin: 0.25rem 0;
  font-weight: 500;
}

/* Conteneur de l'exemple */
.form-field-example-container {
  margin-top: 0.5rem;
}

.form-field-example-text {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  font-style: italic;
  font-weight: 300;
}

/* Override complet de tous les styles Bootstrap qui pourraient interférer */
.form-field-wrapper * {
  box-sizing: border-box;
}

.form-field-wrapper .form-field-label-text,
.form-field-wrapper .form-field-label-container,
.form-field-wrapper .form-field-label-content {
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  padding: 0 !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}

/* S'assurer qu'aucun style Bootstrap n'interfère */
.form-field-wrapper:not(.form-floating) .form-field-label-text,
.form-field-wrapper .form-field-label-text {
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  padding: 0 !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}

/* Forcer la séparation entre label et input */
.form-field-label-container + .form-field-input-container {
  margin-top: 0.75rem !important;
}
</style>
